import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoLinkSuggestions, seoLinkSuggestionAction } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Loading, Empty, DataTable, Stat, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/link-injector")({
  head: () => ({ meta: [{ title: "Internal links · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: LinkInjectorPage,
});

function LinkInjectorPage() {
  const load = useServerFn(seoLinkSuggestions);
  const act = useServerFn(seoLinkSuggestionAction);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-link-suggestions"], queryFn: () => load() });
  const rows = ((q.data as any)?.rows ?? []) as any[];

  const refresh = useMutation({
    mutationFn: () => act({ data: { action: "refresh" } }),
    onSuccess: (r: any) => {
      toast.success(r.state === "no_crawl" ? "Run a site crawl first." : `${r.suggestions} link suggestions ready.`);
      qc.invalidateQueries({ queryKey: ["seo-link-suggestions"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not rebuild suggestions."),
  });

  const decide = useMutation({
    mutationFn: (v: { id: string; action: "propose" | "dismiss" }) => act({ data: v }),
    onSuccess: (r: any) => {
      toast.success(r.dismissed ? "Suggestion dismissed." : `Sent for approval (${r.status ?? "pending"}).`);
      qc.invalidateQueries({ queryKey: ["seo-link-suggestions"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not action that suggestion."),
  });

  return (
    <SeoPage
      title="Internal links"
      description="Pages that deserve more links from the rest of the site, each paired with a page that genuinely covers the same topic and the exact wording to link with. Nothing changes a page until you approve it."
      actions={
        <Button onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          {refresh.isPending ? "Rebuilding…" : "Rebuild suggestions"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Open suggestions" value={num(rows.length)} />
            <Stat label="Pages needing links" value={num(new Set(rows.map((r) => r.to_path)).size)} />
            <Stat label="Top score" value={rows[0] ? Number(rows[0].score).toFixed(2) : "—"} />
          </div>
          <Panel title="Suggested links" subtitle="Anchor wording comes from the destination page's own heading — nothing is invented.">
            {rows.length === 0 ? (
              <Empty>No suggestions yet. Rebuild after a site crawl has run.</Empty>
            ) : (
              <DataTable
                head={["Add a link on", "Pointing to", "Anchor text", "Why", "Score", ""]}
                rows={rows.map((r) => [
                  r.from_path,
                  r.to_path,
                  r.anchor,
                  r.rationale,
                  Number(r.score).toFixed(2),
                  <div key={r.id} className="flex gap-2">
                    <Button size="sm" onClick={() => decide.mutate({ id: r.id, action: "propose" })}>
                      Send for approval
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decide.mutate({ id: r.id, action: "dismiss" })}>
                      Dismiss
                    </Button>
                  </div>,
                ])}
              />
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
